import { EStatusLink } from '@/domain/Availability.domain';
import { TAvailabilityState, TStatus } from './domain';

export const validateSelections = (state: TAvailabilityState): TStatus => {
    const schedule = state.selectedSchedule
        ? state.selectedDayFull?.schedules &&
          Object.values(state.selectedDayFull.schedules).find(
              (schedule) => schedule.id === state.selectedSchedule
          )
        : undefined;

    const tickets =
        (schedule && Object.values(schedule.tickets)) ||
        (state.selectedDayFull?.tickets && Object.values(state.selectedDayFull.tickets)) ||
        undefined;

    const additionalOptions = state.selectedDayFull?.additional_options
        ? Object.values(state.selectedDayFull.additional_options)
        : undefined;

    const terms = state.selectedDayFull?.terms;

    if (typeof state.selectedSchedule === 'undefined') {
        return {
            isValid: false,
            message: 'Please select a schedule.',
            error: false,
            touched: false,
            link: EStatusLink.EMPTY,
        };
    }

    if (tickets && schedule && Number(schedule?.min_tickets) > 1) {
        const totalSelectedCount = tickets.reduce(
            (res, ticket) => res + (state.selectedTickets[ticket.id] || 0),
            0
        );
        if (totalSelectedCount > 0 && totalSelectedCount < Number(schedule.min_tickets)) {
            return {
                isValid: false,
                message: `You need at least ${schedule.min_tickets} ticket(s)`,
                error: false,
                touched: true,
                link: EStatusLink.MIN_TICKETS,
            };
        }
    }

    if (tickets && tickets.length > 0) {
        if (tickets.every((ticket) => !state.selectedTickets[ticket.id])) {
            return {
                isValid: false,
                message: 'Please select the ticket type.',
                error: false,
                touched: true,
                link: EStatusLink.TICKETS,
            };
        }

        for (const ticket of tickets) {
            const min = parseInt(ticket.min);
            const sel = state.selectedTickets[ticket.id] || 0;

            if (min > sel && state.selectedTickets[ticket.id]) {
                return {
                    isValid: false,
                    message: `You Cannot Order Less Than ${min} Tickets for "${ticket.name}" Ticket`,
                    error: false,
                    touched: true,
                    link: EStatusLink.TICKETS,
                };
            }
        }

        if (
            !tickets.some((ticket) => state.selectedTickets[ticket.id] && ticket.is_child === '0')
        ) {
            return {
                isValid: false,
                message: 'You Cannot Order Child Tickets Only',
                error: false,
                touched: true,
                link: EStatusLink.IS_CHILD,
            };
        }

        if (state.needTicketsNames && tickets) {
            const guestsIsNotValid = tickets.some((ticket) => {
                if (!state.selectedTickets[ticket.id]) return false;
                const guestFields = state.ticketNames[ticket.id];
                if (
                    !guestFields ||
                    Object.keys(guestFields).length < state.selectedTickets[ticket.id]
                ) {
                    return true;
                }
                return Object.values(guestFields).some(
                    (guest) => !guest.first_name || !guest.last_name
                );
            });
            if (guestsIsNotValid) {
                return {
                    isValid: false,
                    message: 'Please enter the guest(s) names.',
                    touched: true,
                    error: false,
                    link: EStatusLink.TICKETS,
                };
            }
        }
    }

    if (additionalOptions && additionalOptions.length > 0) {
        const filteredOption = additionalOptions.filter(
            (additionalOption) => !+additionalOption.is_disabled
        );

        for (const additionalOption of filteredOption) {
            const isRequired = Boolean(+additionalOption.required);
            const specifiedValue = state.selectedOptions[+additionalOption.id];
            if (isRequired && !specifiedValue) {
                const isContainOptionWord = /.+option$/i.test(additionalOption.name);
                return {
                    isValid: false,
                    message: `Please specify ${additionalOption.name}${
                        !isContainOptionWord ? ' option' : ''
                    }.`,
                    error: false,
                    touched: true,
                    link: EStatusLink.ADDITIONAL_OPTIONS,
                };
            }
        }
    }

    if (typeof state.gratuity === 'undefined' && state.enableTips) {
        return {
            isValid: false,
            message: 'Please select tip amount.',
            error: false,
            touched: true,
            link: EStatusLink.TIPS,
        };
    }

    if (!state.agreementSelected) {
        return {
            isValid: false,
            message: 'Please agree to the reservation requirements.',
            error: false,
            touched: true,
            link: EStatusLink.CANCELLATION_INFO,
        };
    }

    if (
        !!terms?.length &&
        (state.termsSelected?.length !== terms.length ||
            !state.termsSelected?.every((term) => term === true))
    ) {
        return {
            isValid: false,
            message: 'Please agree to the terms and conditions.',
            error: false,
            touched: true,
            link: EStatusLink.CANCELLATION_INFO,
        };
    }

    return {
        isValid: true,
        message: `Great. You're ready to add to your cart!`,
        error: false,
        touched: true,
        link: EStatusLink.ADD_TO_CART,
    };
};
